import { getCronofyConnectUrl } from '@/api/auth';
import { Layout as BaseLayout, Col, Flex, Grid, Modal, Typography } from 'antd';
import { FC, Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Footer, Header } from './index';
import { ROUTES } from '@/types/routes';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { UserRole } from '@/types';
import { AccessRestricted } from '@/components/modals/AccessRestricted';
import { ReacceptanceModal } from '@/components/modals/Reacceptance';
import { CronofyConnectModal } from './CronofyConnectModal';
import { differenceInMinutes } from 'date-fns';
import { SubscriptionBanner } from '@/components/shared';
import { SvgIcon } from '@/components/icon';
import { CustomButton } from '@/components/ui';
import {
  HEADER_SLOT_CLASS,
  HEADER_SLOT_SCROLLED_CLASS,
} from '@/components/layout/MobileHeader';
import { useIsPageScrolled } from '@/components/layout/MobileHeader/useIsPageScrolled';
import PromoPopup from '@/components/popup/PromoPopup';

const Layout: FC = () => {
  const { lg } = Grid.useBreakpoint();
  const isScrolled = useIsPageScrolled();
  const user = useAppSelector(selectUser);
  const [freeTrialModalOpen, setFreeTrialModalOpen] = useState(false);
  const [accessRestrictedModalOpen, setAccessRestrictedModalOpen] =
    useState(false);
  const [shownThisSession, setShownThisSession] = useState(false);
  const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Public consultant profile view (anonymous visitor on /consultants/:id)
  // suppresses the "Unlock Premium Exposure" banner so the page reads
  // like a marketing surface, not a logged-out app shell. Mirrors the
  // same suppression for the auth-prompting nav items in Header.tsx.
  // See TCX-577.
  const isAnonConsultantProfile =
    !localStorage.getItem('accessToken') &&
    /^\/consultants\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    if (!user || user.role !== UserRole.FRANCHISEE) return;
    if (shownThisSession) return;
    if (!user.stripeSubscriptionStatus) {
      setShownThisSession(true);
      setFreeTrialModalOpen(true);
    }
  }, [user]);

  const isConsultant = user?.role === UserRole.CONSULTANT;
  const isCalendarConnected = Boolean(
    user.cronofyAccessToken &&
      user.cronofyAccountId &&
      user.cronofyConferencingProfileId &&
      user.cronofyRefreshToken &&
      user.cronofyTokenExpiresAt,
  );

  const handleConnectCalendar = () => {
    setShowCronofyModal(false);
    navigate(`${ROUTES.MY_PROFILE_CALENDAR}`);
  };

  useEffect(() => {
    if (window.location.pathname.includes('/my-profile')) return;
    if (!user || user.role !== UserRole.FRANCHISEE) return;
    if (
      user.stripeSubscriptionStatus &&
      user.stripeSubscriptionStatus !== 'active' &&
      user.stripeSubscriptionStatus !== 'trialing' &&
      user.stripeSubscriptionStatus !== 'grace'
    ) {
      setAccessRestrictedModalOpen(true);
    }
  }, [window.location.pathname, user.stripeSubscriptionStatus]);

  // Hide the Ezee Assist chat widget across the whole Messages experience
  // (/chats and any nested route under it) and restore it everywhere else. The
  // widget host lives in the light DOM as a div on <body> (id "ezee-chat-shadow-root"
  // in fork builds, "shadow-root" in legacy vendor builds); the hide rule that
  // targets both ids is keyed off this body class in styles/chat.scss.
  // Toggled purely by route — the rule only matches the host ids, which only
  // exist when the widget is present, so there's no need to gate on role.
  // Cleanup clears the class when leaving the layout.
  useEffect(() => {
    const isChatsPage =
      location.pathname === ROUTES.CHATS ||
      location.pathname.startsWith(`${ROUTES.CHATS}/`);

    document.body.classList.toggle('ezee-assist-hidden', isChatsPage);

    return () => {
      document.body.classList.remove('ezee-assist-hidden');
    };
  }, [location.pathname]);

  const onCancel = () => {
    setFreeTrialModalOpen(false);
  };

  const showBanner = () => {
    const routes = [/^\/consultants+$/, /^\/bookings+$/];

    return routes.some((route) => route.test(window.location.pathname));
  };

  const hideFooter = () => {
    const cases = [ROUTES.CHATS, /^\/chats\/[^/]+$/, /^\/my-profile\/[^/]+$/];

    return cases.some((route) =>
      typeof route === 'string'
        ? route === window.location.pathname
        : route.test(window.location.pathname),
    );
  };

  const isFooterHidden = hideFooter();
  // My Profile sub-pages hide the footer too, but unlike the full-height
  // chat they end in form buttons that need breathing room below them.
  const isProfileSubpage = /^\/my-profile\/[^/]+$/.test(
    window.location.pathname,
  );
  const isBannerVisible = showBanner();

  const CRONOFY_MODAL_KEY = 'cronofy-modal-last-closed';
  const [showCronofyModal, setShowCronofyModal] = useState(false);

  const handleReconnectCalendar = async () => {
    try {
      // SEC-04 (TCX-750): the connect flow is authenticated; the backend binds
      // the consultant to the JWT and returns the OAuth URL to redirect to.
      const url = await getCronofyConnectUrl();
      window.location.href = url;
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isConsultant && !user?.cronofyCalendarId && isCalendarConnected) {
      setReconnectModalOpen(true);
    }
  }, [isConsultant, user?.cronofyCalendarId]);

  useEffect(() => {
    if (!isConsultant || isCalendarConnected) {
      setShowCronofyModal(false);
      return;
    }

    const checkAndShowModal = () => {
      const lastClosed = localStorage.getItem(CRONOFY_MODAL_KEY);
      const now = new Date();
      if (!lastClosed || differenceInMinutes(now, new Date(lastClosed)) >= 15) {
        setShowCronofyModal(true);
      } else {
        setShowCronofyModal(false);
      }
    };

    checkAndShowModal();
    const interval = setInterval(checkAndShowModal, 60 * 1000);
    return () => clearInterval(interval);
  }, [isConsultant, isCalendarConnected]);

  const handleCloseCronofyModal = () => {
    localStorage.setItem(CRONOFY_MODAL_KEY, new Date().toISOString());
    setShowCronofyModal(false);
  };

  return (
    <BaseLayout className={'user-layout'} style={{ minHeight: '100dvh' }}>
      <BaseLayout.Header
        className={
          isScrolled
            ? `${HEADER_SLOT_CLASS} ${HEADER_SLOT_SCROLLED_CLASS}`
            : HEADER_SLOT_CLASS
        }
        style={{
          height: 'auto',
          padding: lg ? '24px 48px' : 0,
          margin: 0,
        }}
      >
        <Header />
      </BaseLayout.Header>
      <BaseLayout.Content
        style={{
          flex: 1,
          padding: lg ? '0 48px' : 0,
          marginBottom: isFooterHidden ? (isProfileSubpage ? 32 : 0) : 150,
        }}
      >
        {user.role === UserRole.FRANCHISEE &&
          isBannerVisible &&
          user.stripeSubscriptionStatus !== 'grace' &&
          user.stripeSubscriptionStatus !== 'active' &&
          user.stripeSubscriptionStatus !== 'trialing' && (
            <Col lg={16} xs={24}>
              <SubscriptionBanner />
            </Col>
          )}
        <Suspense>
          <Outlet />
        </Suspense>
        {isConsultant && !isCalendarConnected && showCronofyModal && (
          <CronofyConnectModal
            open={true}
            onConnect={handleConnectCalendar}
            onClose={handleCloseCronofyModal}
          />
        )}

        <Modal
          open={reconnectModalOpen}
          onCancel={() => setReconnectModalOpen(false)}
          footer={null}
          styles={{
            mask: {
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              zIndex: 99999,
            },
            wrapper: {
              zIndex: 999999,
            },
          }}
          centered
          width={460}
          className={'my-profile-modal'}
        >
          <Flex vertical gap={16} className={'modal-content'}>
            <Typography.Text className={'title'}>
              Reconnect Your Calendar
            </Typography.Text>

            <Typography.Text className={'description'}>
              To schedule a call seamlessly, you need to re-link your calendar.
              Please reconnect using one of the buttons below:
            </Typography.Text>
          </Flex>

          <Flex vertical gap={12}>
            <CustomButton
              type={'primary'}
              text={'Reconnect'}
              onClick={() => handleReconnectCalendar()}
            />
          </Flex>
        </Modal>
      </BaseLayout.Content>
      {!isFooterHidden && (
        <BaseLayout.Footer style={{ padding: lg ? '24px 50px' : '24px' }}>
          <Footer />
        </BaseLayout.Footer>
      )}
      <AccessRestricted
        open={accessRestrictedModalOpen}
        onCancel={() => setAccessRestrictedModalOpen(false)}
      />
      {/* TCX-668: blocking legal re-acceptance. Shown for any consultant/
          franchisor whose accepted legal-doc versions are behind the current
          published ones; cannot be dismissed without accepting. */}
      <ReacceptanceModal
        open={
          !!user?.legalReacceptance?.required &&
          (user.role === UserRole.CONSULTANT ||
            user.role === UserRole.FRANCHISEE)
        }
      />
      {/* TCX-917: one pop-up component for both audiences — the server decides
          which record this user gets from their own role. The `lg` gate and the
          anon-profile exclusion are unchanged. */}
      {lg && !isAnonConsultantProfile && <PromoPopup />}
    </BaseLayout>
  );
};

export { Layout };
